import * as THREE from 'three';
import Feature from 'ol/Feature.js';
import MVT from 'ol/format/MVT.js';
import type Geometry from 'ol/geom/Geometry.js';
import { PMTiles } from 'pmtiles';
import type { Layer } from '@geosoft/viewer-sdk';
import {
  readPmtilesArchiveInfo,
  resolvePmtilesSourceLayerFromInfo,
  type PmtilesArchiveHeaderSummary,
  type PmtilesSourceLayerResolution,
} from '@/engines/pmtiles/sourceLayer';
import { resolveVectorFallbackColor } from '@/design/tokenResolver';

const ORIGIN_SHIFT = 20037508.342789244;
const WEB_MERCATOR_MAX_LAT = 85.0511287798066;
const DEFAULT_VECTOR_ZOOM = 12;
const DEFAULT_MAX_TILE_COUNT = 256;
const DEFAULT_Z_OFFSET_METERS = 0.75;

export interface NativePmtilesTilePlan {
  readonly zoom: number;
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
  readonly tileCount: number;
  readonly cappedByMaxTileCount: boolean;
}

export interface NativePmtilesVectorMetrics {
  readonly archive: PmtilesArchiveHeaderSummary | null;
  readonly sourceLayer: PmtilesSourceLayerResolution;
  readonly tilePlan: NativePmtilesTilePlan | null;
  readonly decodedTileCount: number;
  readonly renderedFeatureCount: number;
  readonly lastTileError: string | null;
  readonly renderFrameAssumptions: readonly string[];
}


export interface NativeVectorStylePatch {
  readonly color?: string;
  readonly fillOpacity?: number;
  readonly lineOpacity?: number;
  readonly lineWidth?: number;
  readonly pointOpacity?: number;
  readonly pointRadius?: number;
  readonly showFills?: boolean;
  readonly showLines?: boolean;
  readonly showPoints?: boolean;
}

export interface NativePmtilesFeatureHit {
  readonly layerId: string;
  readonly featureId: string | number | undefined;
  readonly sourceLayer: string | null;
  readonly properties: Record<string, unknown>;
}

const FEATURE_DATA_KEY = '__pmtiles_feature_data__';
export class NativeGiroPmtilesVectorLayer {
  readonly object: THREE.Group;
  private readonly abort = new AbortController();
  private readonly materials: THREE.Material[] = [];
  private readonly pointVertices: THREE.Vector3[] = [];
  private metrics: NativePmtilesVectorMetrics = {
    archive: null,
    sourceLayer: { declared: null, applied: null, available: [] },
    tilePlan: null,
    decodedTileCount: 0,
    renderedFeatureCount: 0,
    lastTileError: null,
    renderFrameAssumptions: [
      'MVT coordinates are decoded into EPSG:3857 and added to the Giro3D scene as Three.js objects.',
      'Current milestone renders on the map plane with a small z offset; full terrain drape/elevation sampling is not implemented yet.',
      'Depth testing remains enabled so the layer participates in the Giro3D render frame instead of using a separate overlay canvas.',
    ],
  };

  constructor(private readonly layer: Layer) {
    this.object = new THREE.Group();
    this.object.name = `native-pmtiles-vector:${layer.id}`;
    this.object.renderOrder = 20;
  }

  async load(): Promise<void> {
    const url = this.layer.endpoints.pmtiles;
    if (!url) throw new Error(`Layer '${this.layer.id}' is missing endpoints.pmtiles`);
    const archiveInfo = await readPmtilesArchiveInfo(url);
    const sourceLayer = resolvePmtilesSourceLayerFromInfo(this.layer, archiveInfo);
    this.metrics = {
      ...this.metrics,
      archive: archiveInfo.header,
      sourceLayer,
    };
    if (!sourceLayer.applied) {
      throw new Error(`PMTiles source-layer could not be resolved for ${this.layer.id}`);
    }

    const bounds = archiveInfo.header?.bounds ?? plausibleLonLatBounds(this.layer.bbox);
    if (!bounds) {
      throw new Error(`PMTiles archive bounds are unavailable for ${this.layer.id}`);
    }
    const plan = buildNativePmtilesTilePlan({
      bounds,
      minZoom: archiveInfo.header?.minZoom ?? 0,
      maxZoom: archiveInfo.header?.maxZoom ?? 14,
      preferredZoom: readNumber(this.layer.style, 'nativeVectorZoom')
        ?? readNumber(this.layer.style, 'vectorTileZoom')
        ?? DEFAULT_VECTOR_ZOOM,
      maxTileCount: readNumber(this.layer.style, 'nativeVectorMaxTiles') ?? DEFAULT_MAX_TILE_COUNT,
    });
    this.metrics = { ...this.metrics, tilePlan: plan };

    const archive = new PMTiles(url);
    const format = new MVT<Feature<Geometry>>({
      featureClass: Feature,
      layers: [sourceLayer.applied],
    });
    const fillMaterial = this.createFillMaterial();
    const lineMaterial = this.createLineMaterial();

    for (let x = plan.minX; x <= plan.maxX; x += 1) {
      for (let y = plan.minY; y <= plan.maxY; y += 1) {
        if (this.abort.signal.aborted) return;
        await this.loadTile({ archive, format, z: plan.zoom, x, y, fillMaterial, lineMaterial });
      }
    }
    this.flushPoints();
  }

  dispose(): void {
    this.abort.abort();
    this.object.traverse((child) => {
      const mesh = child as THREE.Mesh | THREE.Line | THREE.Points;
      mesh.geometry?.dispose();
    });
    for (const material of this.materials) {
      material.dispose();
    }
    this.materials.length = 0;
    this.object.clear();
  }

  setOpacity(opacity: number): void {
    const clamped = Math.max(0, Math.min(1, opacity));
    for (const material of this.materials) {
      material.opacity = clamped;
      material.transparent = clamped < 1;
      material.needsUpdate = true;
    }
  }

  getMetrics(): NativePmtilesVectorMetrics {
    return this.metrics;
  }

  applyStylePatch(patch: NativeVectorStylePatch): void {
    for (const child of this.object.children) {
      const name = child.name;
      if (name.endsWith(':fill')) {
        if (patch.showFills !== undefined) child.visible = patch.showFills;
        const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
        if (patch.color) mat.color.set(patch.color);
        if (patch.fillOpacity !== undefined) { mat.opacity = patch.fillOpacity; mat.transparent = patch.fillOpacity < 1; mat.needsUpdate = true; }
      } else if (name.endsWith(':line')) {
        if (patch.showLines !== undefined) child.visible = patch.showLines;
        const mat = (child as THREE.Line).material as THREE.LineBasicMaterial;
        if (patch.color) mat.color.set(patch.color);
        if (patch.lineOpacity !== undefined) { mat.opacity = patch.lineOpacity; mat.transparent = patch.lineOpacity < 1; mat.needsUpdate = true; }
        if (patch.lineWidth !== undefined) mat.linewidth = patch.lineWidth;
      } else if (name.endsWith(':points')) {
        if (patch.showPoints !== undefined) child.visible = patch.showPoints;
        const mat = (child as THREE.Points).material as THREE.PointsMaterial;
        if (patch.color) mat.color.set(patch.color);
        if (patch.pointOpacity !== undefined) { mat.opacity = patch.pointOpacity; mat.transparent = patch.pointOpacity < 1; mat.needsUpdate = true; }
        if (patch.pointRadius !== undefined) mat.size = patch.pointRadius;
      }
    }
  }

  static extractFeatureData(object: THREE.Object3D): NativePmtilesFeatureHit | null {
    const data = (object as any)[FEATURE_DATA_KEY];
    return data ?? null;
  }

  private async loadTile(args: {
    readonly archive: PMTiles;
    readonly format: MVT<Feature<Geometry>>;
    readonly z: number;
    readonly x: number;
    readonly y: number;
    readonly fillMaterial: THREE.Material;
    readonly lineMaterial: THREE.Material;
  }): Promise<void> {
    try {
      const tile = await args.archive.getZxy(args.z, args.x, args.y, this.abort.signal);
      if (!tile?.data || this.abort.signal.aborted) return;
      const features = args.format.readFeatures(tile.data, {
        extent: mercatorTileExtent(args.z, args.x, args.y),
        featureProjection: 'EPSG:3857',
      });
      this.metrics = {
        ...this.metrics,
        decodedTileCount: this.metrics.decodedTileCount + 1,
      };
      for (const feature of features) {
        this.addFeature(feature, args.fillMaterial, args.lineMaterial);
      }
    } catch (err) {
      if (this.abort.signal.aborted) return;
      const message = err instanceof Error ? err.message : String(err);
      this.metrics = { ...this.metrics, lastTileError: message };
      console.warn('[NativeGiroPmtilesVectorLayer] tile decode failed', {
        layerId: this.layer.id,
        z: args.z,
        x: args.x,
        y: args.y,
        error: err,
      });
    }
  }

  private addFeature(
    feature: Feature<Geometry>,
    fillMaterial: THREE.Material,
    lineMaterial: THREE.Material,
  ): void {
    const geometry = feature.getGeometry();
    if (!geometry) return;
    const z = readNumber(this.layer.style, 'zOffsetMeters') ?? DEFAULT_Z_OFFSET_METERS;
    const coords = (geometry as GeometryWithCoordinates).getCoordinates();
    const before = this.object.children.length + this.pointVertices.length;
    switch (geometry.getType()) {
      case 'Point':
        this.addPoint(coords, z + 0.2);
        break;
      case 'MultiPoint':
        for (const point of asArray(coords)) this.addPoint(point, z + 0.2);
        break;
      case 'LineString':
        this.addLineString(coords, z + 0.15, lineMaterial);
        break;
      case 'MultiLineString':
        for (const line of asArray(coords)) this.addLineString(line, z + 0.15, lineMaterial);
        break;
      case 'Polygon':
        this.addPolygon(coords, z, fillMaterial, lineMaterial);
        break;
      case 'MultiPolygon':
        for (const polygon of asArray(coords)) this.addPolygon(polygon, z, fillMaterial, lineMaterial);
        break;
      default:
        break;
    }
    const after = this.object.children.length + this.pointVertices.length;
    if (after > before) {
      this.metrics = {
        ...this.metrics,
        renderedFeatureCount: this.metrics.renderedFeatureCount + 1,
      };
      const hit: NativePmtilesFeatureHit = {
        layerId: this.layer.id,
        featureId: feature.getId?.(),
        sourceLayer: this.metrics.sourceLayer.applied,
        properties: feature.getProperties?.() ?? {},
      };
      for (let i = before; i < this.object.children.length; i++) {
        (this.object.children[i] as any)[FEATURE_DATA_KEY] = hit;
      }
    }
  }

  private addPoint(coord: unknown, z: number): void {
    const xy = asPosition(coord);
    if (!xy) return;
    this.pointVertices.push(new THREE.Vector3(xy[0], xy[1], z));
  }

  private addLineString(coords: unknown, z: number, material: THREE.Material): void {
    const points = asArray(coords)
      .map(asPosition)
      .filter((p): p is readonly [number, number] => Boolean(p))
      .map(([x, y]) => new THREE.Vector3(x, y, z));
    if (points.length < 2) return;
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line = new THREE.Line(geometry, material);
    line.name = `${this.object.name}:line`;
    this.object.add(line);
  }

  private addPolygon(
    coords: unknown,
    z: number,
    fillMaterial: THREE.Material,
    lineMaterial: THREE.Material,
  ): void {
    const rings = asArray(coords).map((ring) =>
      asArray(ring)
        .map(asPosition)
        .filter((p): p is readonly [number, number] => Boolean(p)),
    ).filter((ring) => ring.length >= 4);
    if (rings.length === 0) return;

    const outer = rings[0];
    const shape = new THREE.Shape(outer.map(([x, y]) => new THREE.Vector2(x, y)));
    for (const hole of rings.slice(1)) {
      shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
    }
    const fill = new THREE.ShapeGeometry(shape);
    fill.translate(0, 0, z);
    const mesh = new THREE.Mesh(fill, fillMaterial);
    mesh.name = `${this.object.name}:fill`;
    this.object.add(mesh);

    for (const ring of rings) {
      this.addLineString(ring, z + 0.12, lineMaterial);
    }
  }

  private flushPoints(): void {
    if (this.pointVertices.length === 0) return;
    const geometry = new THREE.BufferGeometry().setFromPoints(this.pointVertices);
    const points = new THREE.Points(geometry, this.createPointMaterial());
    points.name = `${this.object.name}:points`;
    this.object.add(points);
  }

  private createFillMaterial(): THREE.MeshBasicMaterial {
    const opacity = readNumber(this.layer.style, 'fillOpacity') ?? 0.24;
    const material = new THREE.MeshBasicMaterial({
      color: readColor(this.layer),
      opacity,
      transparent: opacity < 1,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false,
    });
    this.materials.push(material);
    return material;
  }

  private createLineMaterial(): THREE.LineBasicMaterial {
    const opacity = readNumber(this.layer.style, 'lineOpacity') ?? 1;
    const lineWidth = readNumber(this.layer.style, 'lineWidth') ?? (isContourLayer(this.layer) ? 1 : 2);
    const material = new THREE.LineBasicMaterial({
      color: readColor(this.layer),
      opacity,
      transparent: opacity < 1,
      linewidth: lineWidth,
      depthTest: true,
      depthWrite: false,
    });
    this.materials.push(material);
    return material;
  }

  private createPointMaterial(): THREE.PointsMaterial {
    const opacity = readNumber(this.layer.style, 'pointOpacity') ?? 1;
    const material = new THREE.PointsMaterial({
      color: readColor(this.layer),
      opacity,
      transparent: opacity < 1,
      size: readNumber(this.layer.style, 'pointRadius') ?? 5,
      sizeAttenuation: false,
      depthTest: true,
      depthWrite: false,
    });
    this.materials.push(material);
    return material;
  }
}

interface GeometryWithCoordinates extends Geometry {
  getCoordinates(): unknown;
}

export function buildNativePmtilesTilePlan(args: {
  readonly bounds: readonly [number, number, number, number];
  readonly minZoom: number;
  readonly maxZoom: number;
  readonly preferredZoom: number;
  readonly maxTileCount: number;
}): NativePmtilesTilePlan {
  const minZoom = Math.max(0, Math.floor(args.minZoom));
  const maxZoom = Math.max(minZoom, Math.floor(args.maxZoom));
  const maxTileCount = Math.max(1, Math.floor(args.maxTileCount));
  let zoom = Math.max(minZoom, Math.min(maxZoom, Math.floor(args.preferredZoom)));
  let range = tileRangeForBounds(args.bounds, zoom);
  let cappedByMaxTileCount = false;
  while (range.tileCount > maxTileCount && zoom > minZoom) {
    cappedByMaxTileCount = true;
    zoom -= 1;
    range = tileRangeForBounds(args.bounds, zoom);
  }
  return { zoom, ...range, cappedByMaxTileCount };
}

export function mercatorTileExtent(z: number, x: number, y: number): [number, number, number, number] {
  const n = 2 ** z;
  const tileSize = (ORIGIN_SHIFT * 2) / n;
  const minX = -ORIGIN_SHIFT + x * tileSize;
  const maxX = minX + tileSize;
  const maxY = ORIGIN_SHIFT - y * tileSize;
  const minY = maxY - tileSize;
  return [minX, minY, maxX, maxY];
}

function tileRangeForBounds(
  bounds: readonly [number, number, number, number],
  zoom: number,
): Omit<NativePmtilesTilePlan, 'zoom' | 'cappedByMaxTileCount'> {
  const n = 2 ** zoom;
  const [minLon, minLat, maxLon, maxLat] = bounds;
  const nw = lonLatToTile(minLon, maxLat, zoom);
  const se = lonLatToTile(maxLon, minLat, zoom);
  const minX = clampInt(Math.min(nw.x, se.x), 0, n - 1);
  const maxX = clampInt(Math.max(nw.x, se.x), 0, n - 1);
  const minY = clampInt(Math.min(nw.y, se.y), 0, n - 1);
  const maxY = clampInt(Math.max(nw.y, se.y), 0, n - 1);
  return {
    minX,
    maxX,
    minY,
    maxY,
    tileCount: (maxX - minX + 1) * (maxY - minY + 1),
  };
}

function lonLatToTile(lon: number, lat: number, zoom: number): { x: number; y: number } {
  const n = 2 ** zoom;
  const clampedLat = Math.max(-WEB_MERCATOR_MAX_LAT, Math.min(WEB_MERCATOR_MAX_LAT, lat));
  const latRad = (clampedLat * Math.PI) / 180;
  return {
    x: Math.floor(((lon + 180) / 360) * n),
    y: Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n),
  };
}

function plausibleLonLatBounds(
  bbox: readonly number[] | undefined,
): readonly [number, number, number, number] | null {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const [minLon, minLat, maxLon, maxLat] = bbox;
  if (![minLon, minLat, maxLon, maxLat].every(Number.isFinite)) return null;
  if (minLon < -180 || maxLon > 180 || minLat < -90 || maxLat > 90 || maxLon <= minLon || maxLat <= minLat) {
    return null;
  }
  return [minLon, minLat, maxLon, maxLat];
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asPosition(value: unknown): readonly [number, number] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const [x, y] = value;
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y)
    ? [x, y]
    : null;
}

function clampInt(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.floor(value)));
}

function readNumber(style: Record<string, unknown> | undefined, key: string): number | null {
  const value = style?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function readColor(layer: Layer): string {
  const value = layer.style?.['color'];
  return typeof value === 'string' && value.length > 0 ? value : resolveVectorFallbackColor();
}

function isContourLayer(layer: Layer): boolean {
  return layer.style?.['terrainDerivative'] === 'contours' || layer.style?.['intakeKind'] === 'dem-contours';
}
